import { useEffect, useState } from 'react';
import { Export, LinkSimple } from '@phosphor-icons/react';
import type { Conversation, PublicUser } from '@sirocco/shared';
import { messages as messagesApi, users as usersApi } from '../../api/endpoints';
import type { ChoseAPartager } from '../../firebase/messages';
import { partager } from '../../utils/partage';
import { Avatar, GroupAvatar } from './Avatar';
import { SearchField } from './Field';
import { Sheet, SheetAction } from './Sheet';
import type { Ancre } from './Popover';
import { t } from '../../i18n';

/**
 * Partager, d'abord dans Sirocco.
 *
 * La feuille du systeme fait tres bien son travail — Messages, WhatsApp,
 * copier le lien — mais elle envoie hors de Sirocco. Or ce qu'on partage
 * le plus souvent, c'est a quelqu'un qui est deja la : lui ouvrir
 * WhatsApp pour lui envoyer un lien qui le ramene ici n'a pas de sens.
 *
 * Les gens viennent donc en premier, et le reste du telephone en dessous.
 * C'est l'ordre de toutes les applications ou l'on partage vraiment, et
 * c'est aussi l'ordre de l'usage.
 *
 * # Les groupes avec les amis, dans la meme bande
 *
 * Une sortie s'envoie plus souvent a ceux qui iront ensemble qu'a une
 * seule personne, et les separer en deux listes aurait fait choisir un
 * rayon avant de choisir un destinataire. Ce sont les memes fils : le
 * carre arrondi d'un groupe le distingue du rond d'un visage, comme dans
 * la liste des messages.
 *
 * Le partage est un message : il arrive dans la discussion, avec le lien.
 * Rien de nouveau a lire pour l'ecran des messages, et une conversation
 * directe s'ouvre si elle n'existait pas.
 *
 * La feuille ne sait pas ce qu'elle envoie — un post, un evenement — et
 * n'a pas a le savoir : elle recoit ce qui voyage et le lien qui va
 * dehors, et c'est tout ce qui change entre les deux.
 */
/** Ou peut aller un partage : quelqu'un, ou un groupe. */
type Cible =
  | { genre: 'ami'; cle: string; nom: string; ami: PublicUser }
  | { genre: 'groupe'; cle: string; nom: string; conversationId: string; photo: string | null };

export function PartageSheet({
  open,
  onClose,
  anchor,
  quoi,
  lien,
  texte,
  onFait,
}: {
  open: boolean;
  onClose: () => void;
  anchor?: Ancre;
  /** Ce qui part a un ami : c'est l'objet qui voyage, pas son adresse. */
  quoi: ChoseAPartager | null;
  /** Le lien public, pour tout ce qui sort de Sirocco. */
  lien: string;
  /** Le texte a annoncer, pour la feuille du systeme. */
  texte?: string;
  /** Ce qu'il faut dire une fois la feuille refermee. */
  onFait: (message: string) => void;
}) {
  const [cibles, setCibles] = useState<Cible[] | null>(null);
  /* Le temps de l'envoi : deux appuis sur le meme visage n'envoient pas
     deux fois, et l'on voit que quelque chose se passe. */
  const [envoiVers, setEnvoiVers] = useState<string | null>(null);
  /* La recherche, pour viser quelqu'un plutot que le chercher des yeux :
     passe une dizaine de fils, la bande devient un defilement. */
  const [cherche, setCherche] = useState('');

  /*
    Les destinations ne se chargent qu'a l'ouverture : la liste ne sert a
    rien tant que la feuille est fermee, et elle doit etre fraiche quand
    elle s'ouvre.

    Les deux lectures partent ensemble et echouent chacune de leur cote :
    ne plus pouvoir lire ses groupes ne doit pas emporter ses amis.
  */
  useEffect(() => {
    if (!open) return;
    let vivant = true;
    void Promise.all([
      usersApi.friendList().catch(() => [] as PublicUser[]),
      messagesApi.conversations().catch(() => [] as Conversation[]),
    ]).then(([liste, fils]) => {
      if (!vivant) return;
      setCibles([
        ...liste.map(
          (ami): Cible => ({ genre: 'ami', cle: `ami:${ami.id}`, nom: ami.username, ami }),
        ),
        ...fils
          .filter((fil) => fil.type === 'group')
          .map(
            (fil): Cible => ({
              genre: 'groupe',
              cle: `groupe:${fil.id}`,
              nom: fil.title,
              conversationId: fil.id,
              photo: fil.avatarUrl,
            }),
          ),
      ]);
    });
    return () => {
      vivant = false;
    };
  }, [open]);

  async function envoyer(cible: Cible) {
    if (envoiVers || !quoi) return;
    setEnvoiVers(cible.cle);
    try {
      if (cible.genre === 'groupe') await messagesApi.partagerDans(cible.conversationId, quoi);
      else await messagesApi.partagerA(cible.ami.id, quoi);
      onClose();
      onFait(t.composants.partage.envoyeA(cible.nom));
    } catch {
      onFait(t.composants.partage.pasParti);
    } finally {
      setEnvoiVers(null);
    }
  }

  async function ailleurs() {
    onClose();
    const issue = await partager({ titre: 'Sirocco', texte, url: lien });
    if (issue === 'copie') onFait(t.composants.partage.lienCopie);
    if (issue === 'echec') onFait(t.composants.partage.impossible);
  }

  async function copier() {
    onClose();
    try {
      await navigator.clipboard.writeText(lien);
      onFait(t.composants.partage.lienCopie);
    } catch {
      onFait(t.composants.partage.copieImpossible);
    }
  }

  /* Le filtre se fait ici : la liste est deja en main, et une recherche
     qui repasse par la base clignoterait a chaque lettre. */
  const retenus = (cibles ?? []).filter((cible) =>
    cible.nom.toLowerCase().includes(cherche.trim().toLowerCase()),
  );

  return (
    <Sheet open={open} onClose={onClose} anchor={anchor} title={t.composants.partage.titre} large>
      {cibles !== null && cibles.length > 0 && (
        <SearchField
          value={cherche}
          placeholder={t.composants.partage.chercher}
          onChange={(event) => setCherche(event.target.value)}
          onClear={() => setCherche('')}
          className="partage__recherche"
        />
      )}

      {cibles === null ? (
        <p className="partage__vide">{t.composants.partage.unInstant}</p>
      ) : cibles.length === 0 ? (
        <p className="partage__vide">
          {t.composants.partage.sansCible}
        </p>
      ) : retenus.length === 0 ? (
        <p className="partage__vide">{t.composants.partage.aucun}</p>
      ) : (
        <div className="partage__cibles">
          {retenus.map((cible) => (
            <button
              key={cible.cle}
              type="button"
              className="partage__cible"
              disabled={envoiVers !== null}
              onClick={() => void envoyer(cible)}
            >
              {cible.genre === 'groupe' ? (
                <GroupAvatar size={54} rounded photoUrl={cible.photo} />
              ) : (
                <Avatar user={cible.ami} size={54} />
              )}
              <span className="partage__nom">
                {envoiVers === cible.cle ? t.composants.partage.envoi : cible.nom}
              </span>
            </button>
          ))}
        </div>
      )}

      <SheetAction icon={<Export size={20} />} onClick={() => void ailleurs()}>
        {t.composants.partage.ailleurs}
      </SheetAction>
      <SheetAction icon={<LinkSimple size={20} />} onClick={() => void copier()}>
        {t.composants.partage.copier}
      </SheetAction>
    </Sheet>
  );
}
